import React from 'react';
import { X, Clock, UserPlus } from 'lucide-react';

// Sample data for demo purposes
const PRIORITY_MESSAGES_DATA = [
  { title: 'Empire State Lease - Urgent Review', priority: 'URGENT', color: 'red', desc: 'Tech Corp requesting contract revision for $2.5M lease agreement', time: '10 days no reply', contact: 'Sarah Mitchell', value: '$2.5M' },
  { title: 'Blackstone Group - Investment Query', priority: 'HIGH', color: 'orange', desc: '$50M portfolio opportunity discussion - requires immediate response', time: '5 days pending', contact: 'John Martinez', value: '$50M' },
  { title: 'Brooklyn Warehouse - Client Concern', priority: 'MEDIUM', color: 'yellow', desc: 'Buyer raising inspection issues on $4.2M property', time: '3 days ago', contact: 'Emily Chen', value: '$4.2M' },
  { title: 'Madison Ave Office - Tenant Request', priority: 'HIGH', color: 'orange', desc: 'Existing tenant requesting lease extension terms', time: '2 days ago', contact: 'Alex Rivera', value: '$6.2M' },
  { title: 'SoHo Retail Space - Negotiation', priority: 'URGENT', color: 'red', desc: 'Landlord counter-offer requires immediate response', time: '1 day ago', contact: 'Michael Wong', value: '$3.8M' },
  { title: 'Tribeca Development - Investor Meeting', priority: 'HIGH', color: 'orange', desc: 'Schedule follow-up meeting with potential investors', time: '4 hours ago', contact: 'Sarah Mitchell', value: '$18M' },
  { title: 'Chelsea Tower - Documentation', priority: 'MEDIUM', color: 'yellow', desc: 'Missing documents for closing scheduled next week', time: '6 hours ago', contact: 'John Martinez', value: '$15M' },
  { title: 'Upper East Side - Showing Feedback', priority: 'MEDIUM', color: 'yellow', desc: 'VIP client feedback pending on luxury penthouse', time: '12 hours ago', contact: 'Emily Chen', value: '$9.8M' }
];

const RECENT_ACTIVITY_DATA = [
  { title: 'Chelsea Tower Deal - Closed', status: 'RESOLVED', color: 'green', desc: 'Contract signed and finalized, $15M luxury development deal completed', time: '2 hours ago', contact: 'Sarah Mitchell', type: 'Deal Closing' },
  { title: 'Madison Ave - Proposal Sent', status: 'ACTIVE', color: 'blue', desc: '$6.2M commercial property proposal delivered to potential buyer', time: '5 hours ago', contact: 'John Martinez', type: 'Proposal' },
  { title: 'SoHo Retail - Follow-up Complete', status: 'RESOLVED', color: 'green', desc: 'All client questions answered regarding $3.8M retail space', time: '1 day ago', contact: 'Emily Chen', type: 'Follow-up' },
  { title: 'Brooklyn Warehouse - Site Visit', status: 'COMPLETED', color: 'green', desc: 'Conducted property tour for $4.2M industrial space buyer', time: '1 day ago', contact: 'Alex Rivera', type: 'Site Visit' },
  { title: 'Tribeca Loft - Negotiation Round 2', status: 'IN PROGRESS', color: 'blue', desc: 'Second round of negotiations for $4.5M luxury loft underway', time: '2 days ago', contact: 'Michael Wong', type: 'Negotiation' },
  { title: 'Upper East Side - Marketing Launch', status: 'COMPLETED', color: 'green', desc: 'Successfully launched marketing campaign for $9.8M penthouse', time: '2 days ago', contact: 'Emily Chen', type: 'Marketing' },
  { title: 'Downtown Office - Lease Renewal', status: 'ACTIVE', color: 'blue', desc: 'Processing $7.5M office lease renewal with existing tenant', time: '3 days ago', contact: 'Sarah Mitchell', type: 'Lease' },
  { title: 'Midtown Plaza - Inspection Complete', status: 'RESOLVED', color: 'green', desc: 'Building inspection completed for $22M mixed-use development', time: '3 days ago', contact: 'John Martinez', type: 'Inspection' }
];

const TEAM_DATA = [
  { name: 'Sarah Mitchell', role: 'Senior Broker', threads: 45, emails: 127, messages: 89, calls: 34, meetings: 18, response: '2.3 hrs' },
  { name: 'John Martinez', role: 'Deal Manager', threads: 38, emails: 98, messages: 72, calls: 41, meetings: 22, response: '3.1 hrs' },
  { name: 'Alex Rivera', role: 'Property Manager', threads: 52, emails: 156, messages: 103, calls: 28, meetings: 31, response: '1.8 hrs' },
  { name: 'Emily Chen', role: 'Marketing Director', threads: 31, emails: 84, messages: 61, calls: 19, meetings: 15, response: '4.2 hrs' },
  { name: 'Michael Wong', role: 'Financial Analyst', threads: 29, emails: 76, messages: 54, calls: 23, meetings: 19, response: '2.9 hrs' }
];

const CommWidgetModal = ({
  isOpen,
  onClose,
  modalType // 'priorityMessages' | 'recentActivity' | 'teamCommunications'
}) => {
  if (!isOpen) return null;

  const getTitle = () => {
    switch (modalType) {
      case 'priorityMessages':
        return 'All Priority Messages & Follow-ups';
      case 'recentActivity':
        return 'Complete Recent Activity Log';
      case 'teamCommunications':
        return 'Team Communications - Detailed View';
      default:
        return '';
    }
  };

  const getPriorityBadgeClass = (color) => {
    switch (color) {
      case 'red':
        return 'bg-red-500/20 text-red-400';
      case 'orange':
        return 'bg-orange-500/20 text-orange-400';
      default:
        return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  const getStatusBadgeClass = (color) => {
    return color === 'green' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400';
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-black border border-gray-400 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto hover:border-gray-300 transition-all duration-300" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-black border-b border-gray-400 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <h2 className="text-2xl font-bold text-white mokoto-font">
            {getTitle()}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-white hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          >
            <X size={16} className="text-black" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {modalType === 'priorityMessages' && (
            <div className="space-y-4">
              <p className="text-gray-400 mb-4">All messages requiring urgent attention and follow-up</p>
              {PRIORITY_MESSAGES_DATA.map((msg, idx) => (
                <div key={idx} className="bg-black border border-gray-400 rounded-lg p-5 hover:border-gray-300 transition-all duration-300">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{msg.title}</h3>
                      <p className="text-sm text-gray-400 mb-3">{msg.desc}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-400 flex items-center gap-1">
                          <Clock size={14} />
                          {msg.time}
                        </span>
                        <span className="text-gray-400 flex items-center gap-1">
                          <UserPlus size={14} />
                          {msg.contact}
                        </span>
                        <span className="text-green-400 font-semibold">{msg.value}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPriorityBadgeClass(msg.color)}`}>
                      {msg.priority}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {modalType === 'recentActivity' && (
            <div className="space-y-4">
              <p className="text-gray-400 mb-4">Complete log of all recent communication activities and updates</p>
              {RECENT_ACTIVITY_DATA.map((activity, idx) => (
                <div key={idx} className="bg-black border border-gray-400 rounded-lg p-5 hover:border-gray-300 transition-all duration-300">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-white">{activity.title}</h3>
                        <span className="px-2 py-1 bg-gray-800 text-gray-400 rounded text-xs">{activity.type}</span>
                      </div>
                      <p className="text-sm text-gray-400 mb-3">{activity.desc}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {activity.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <UserPlus size={14} />
                          {activity.contact}
                        </span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(activity.color)}`}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {modalType === 'teamCommunications' && (
            <div className="space-y-6">
              <p className="text-gray-400 mb-4">Detailed communication activity breakdown for each team member</p>
              {TEAM_DATA.map((member, idx) => (
                <div key={idx} className="bg-black border border-gray-400 rounded-lg p-6 hover:border-gray-300 transition-all duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-1">{member.name}</h3>
                      <p className="text-sm text-gray-400">{member.role}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-white">{member.threads}</div>
                      <div className="text-xs text-gray-400">Active Threads</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-black border border-gray-400 rounded">
                      <div className="text-xl font-bold text-blue-400">{member.emails}</div>
                      <div className="text-xs text-gray-400 mt-1">Emails</div>
                    </div>
                    <div className="text-center p-3 bg-black border border-gray-400 rounded">
                      <div className="text-xl font-bold text-green-400">{member.messages}</div>
                      <div className="text-xs text-gray-400 mt-1">Messages</div>
                    </div>
                    <div className="text-center p-3 bg-black border border-gray-400 rounded">
                      <div className="text-xl font-bold text-purple-400">{member.calls}</div>
                      <div className="text-xs text-gray-400 mt-1">Calls</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-400">
                    <span className="text-sm text-gray-400">Meetings This Week</span>
                    <span className="text-lg font-semibold text-white">{member.meetings}</span>
                    <span className="text-sm text-gray-400">Avg Response Time</span>
                    <span className="text-lg font-semibold text-cyan-400">{member.response}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommWidgetModal;
